import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Chip,
  Tabs,
  Tab,
  Grid
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { enGB } from 'date-fns/locale';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import CancelIcon from '@mui/icons-material/Cancel';
import api from '../../utils/api';

// DatePicker styling to match theme
const DatePickerWrapper = styled('div')(({ theme }) => ({
  '& .react-datepicker-popper': {
    zIndex: 99999,
    position: 'fixed !important',
  },
  '& .react-datepicker': {
    fontFamily: theme.typography.fontFamily,
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: '8px',
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
    fontSize: '14px',
    zIndex: 99999,
    '& .react-datepicker__header': {
      backgroundColor: theme.palette.primary.main,
      color: 'white',
      borderBottom: 'none',
      borderRadius: '8px 8px 0 0',
      padding: '8px 0',
    },
    '& .react-datepicker__current-month': {
      color: 'white',
      fontWeight: 'bold',
      fontSize: '16px',
    },
    '& .react-datepicker__day-name': {
      color: 'white',
      fontSize: '12px',
      fontWeight: 'bold',
    },
    '& .react-datepicker__navigation': {
      top: '12px',
      '&--previous': {
        borderRightColor: 'white',
      },
      '&--next': {
        borderLeftColor: 'white',
      },
    },
    '& .react-datepicker__day': {
      fontSize: '14px',
      margin: '2px',
      width: '32px',
      height: '32px',
      lineHeight: '32px',
      '&:hover': {
        backgroundColor: theme.palette.primary.light,
        color: 'white',
        borderRadius: '4px',
      },
    },
    '& .react-datepicker__day--selected': {
      backgroundColor: theme.palette.primary.main,
      color: 'white',
      borderRadius: '4px',
      fontWeight: 'bold',
    },
    '& .react-datepicker__time-container': {
      borderLeft: `1px solid ${theme.palette.grey[300]}`,
    },
    '& .react-datepicker__time-list-item--selected': {
      backgroundColor: theme.palette.primary.main,
      color: 'white',
      fontWeight: 'bold',
    },
  },
  '& .react-datepicker-wrapper': {
    width: '100%',
    display: 'block'
  },
  '& .react-datepicker__input-container input': {
    width: '100%',
    padding: '12px',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '4px',
    fontSize: '14px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: 'rgba(255, 255, 255, 0.9)',
    '&::placeholder': {
      color: 'rgba(255, 255, 255, 0.5)'
    },
    '&:focus': {
      outline: 'none',
      borderColor: 'rgba(255, 255, 255, 0.5)',
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      boxShadow: '0 0 0 2px rgba(255, 255, 255, 0.1)'
    }
  }
}));

interface Counsellor {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  applicationStatus: string;
}

interface GroupSupervisionSession {
  _id: string;
  supervisorName: string;
  supervisorEmail: string;
  sessionDate: string;
  counsellors: Array<{
    counsellorId: string;
    name: string;
    email: string;
    phone: string;
  }>;
  status: 'scheduled' | 'completed' | 'cancelled';
  notes?: string;
  createdAt: string;
}

const GroupSupervisionManagement: React.FC = () => {
  const [sessions, setSessions] = useState<GroupSupervisionSession[]>([]);
  const [counsellors, setCounsellors] = useState<Counsellor[]>([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCounsellors, setSelectedCounsellors] = useState<string[]>([]);
  const [currentTab, setCurrentTab] = useState(0);
  
  const [formData, setFormData] = useState({
    supervisorName: '',
    supervisorEmail: '',
    sessionDate: new Date(),
    notes: ''
  });
  
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchSessions();
    fetchCounsellors();
  }, []);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/group-supervision');
      setSessions(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch sessions');
    } finally {
      setLoading(false);
    }
  };

  const fetchCounsellors = async () => {
    try {
      const response = await api.get('/api/counsellors');
      const activeCounsellors = response.data.filter(
        (c: Counsellor) => c.applicationStatus === 'Active'
      );
      setCounsellors(activeCounsellors);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch counsellors');
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setError(null);
    setSuccess(null);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({
      supervisorName: '',
      supervisorEmail: '',
      sessionDate: new Date(),
      notes: ''
    });
    setSelectedCounsellors([]);
    setError(null);
  };

  const handleCounsellorToggle = (counsellorId: string) => {
    setSelectedCounsellors(prev => 
      prev.includes(counsellorId)
        ? prev.filter(id => id !== counsellorId)
        : [...prev, counsellorId]
    );
  };

  const handleSelectAll = () => {
    if (selectedCounsellors.length === counsellors.length) {
      setSelectedCounsellors([]);
    } else {
      setSelectedCounsellors(counsellors.map(c => c._id));
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async () => {
    if (!formData.supervisorName.trim()) {
      setError('Supervisor name is required');
      return;
    }

    if (!formData.supervisorEmail.trim() || !validateEmail(formData.supervisorEmail)) {
      setError('Valid supervisor email is required');
      return;
    }

    if (selectedCounsellors.length === 0) {
      setError('Please select at least one counsellor');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await api.post('/api/group-supervision', {
        supervisorName: formData.supervisorName,
        supervisorEmail: formData.supervisorEmail,
        sessionDate: formData.sessionDate.toISOString(),
        counsellorIds: selectedCounsellors,
        notes: formData.notes
      });

      setSuccess('Group supervision session created and emails sent successfully!');
      handleCloseDialog();
      fetchSessions();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create session');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'cancelled':
        return <CancelIcon color="error" />;
      default:
        return <PendingIcon color="primary" />;
    }
  };

  const filterSessionsByTab = (sessions: GroupSupervisionSession[]) => {
    const now = new Date();
    switch (currentTab) {
      case 0: // Upcoming
        return sessions.filter(s => 
          s.status === 'scheduled' && new Date(s.sessionDate) >= now
        );
      case 1: // Past
        return sessions.filter(s => 
          s.status === 'completed' || 
          s.status === 'cancelled' || 
          (s.status === 'scheduled' && new Date(s.sessionDate) < now)
        );
      default:
        return sessions;
    }
  };

  const filteredSessions = filterSessionsByTab(sessions);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography 
          variant="h4" 
          className="theme-text-primary"
          sx={{ fontWeight: 700 }}
        >
          Group Supervision Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
          sx={{
            backgroundColor: 'rgba(76, 175, 80, 0.2)',
            color: 'rgba(255, 255, 255, 0.9)',
            border: '1px solid rgba(76, 175, 80, 0.3)',
            '&:hover': {
              backgroundColor: 'rgba(76, 175, 80, 0.3)',
              borderColor: 'rgba(76, 175, 80, 0.5)'
            }
          }}
        >
          Schedule Group Supervision
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <div className="mb-6 p-4 glass-card-light border-l-4 border-red-400">
          <Typography variant="body1" className="text-red-600 font-semibold">
            {error}
          </Typography>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 glass-card-light border-l-4 border-green-400">
          <Typography variant="body1" className="text-green-600 font-semibold">
            {success}
          </Typography>
        </div>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={currentTab} 
          onChange={(_, newValue) => setCurrentTab(newValue)}
          sx={{
            '& .MuiTab-root': {
              color: 'rgba(255, 255, 255, 0.7)',
              fontWeight: 600,
              fontSize: '1rem',
              textTransform: 'none',
              minWidth: 180
            },
            '& .Mui-selected': {
              color: 'rgba(255, 255, 255, 1)'
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#64b5f6'
            }
          }}
        >
          <Tab label="Upcoming Sessions" />
          <Tab label="Past Sessions" />
        </Tabs>
      </Box>

      {/* Table */}
      {loading && !openDialog ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
        </Box>
      ) : (
        <div className="overflow-x-auto glass-card">
          <table className="min-w-full">
            <thead className="glass-card-light">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold theme-text-secondary uppercase tracking-wider">
                  Date & Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold theme-text-secondary uppercase tracking-wider">
                  Supervisor
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold theme-text-secondary uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold theme-text-secondary uppercase tracking-wider">
                  Counsellors
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold theme-text-secondary uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {filteredSessions.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center">
                    <Typography className="theme-text-muted">
                      No {currentTab === 0 ? 'upcoming' : 'past'} sessions found
                    </Typography>
                  </td>
                </tr>
              ) : (
                filteredSessions.map((session) => (
                  <tr key={session._id} className="hover:bg-white/5 transition-colors duration-200">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm theme-text-primary">
                        {new Date(session.sessionDate).toLocaleString('en-GB', {
                          weekday: 'short',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium theme-text-primary">
                        {session.supervisorName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm theme-text-muted">
                        {session.supervisorEmail}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {session.counsellors.map((c, idx) => (
                          <Chip
                            key={idx}
                            label={c.name}
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(100, 181, 246, 0.2)',
                              color: 'rgba(255, 255, 255, 0.9)',
                              border: '1px solid rgba(100, 181, 246, 0.3)',
                              fontSize: '0.75rem'
                            }}
                          />
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Chip
                        icon={getStatusIcon(session.status)}
                        label={session.status.charAt(0).toUpperCase() + session.status.slice(1)}
                        size="small"
                        sx={{
                          backgroundColor: session.status === 'completed' 
                            ? 'rgba(76, 175, 80, 0.2)' 
                            : session.status === 'cancelled'
                            ? 'rgba(244, 67, 54, 0.2)'
                            : 'rgba(100, 181, 246, 0.2)',
                          color: 'rgba(255, 255, 255, 0.9)',
                          border: `1px solid ${session.status === 'completed' 
                            ? 'rgba(76, 175, 80, 0.3)' 
                            : session.status === 'cancelled'
                            ? 'rgba(244, 67, 54, 0.3)'
                            : 'rgba(100, 181, 246, 0.3)'}`
                        }}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: 'rgba(255, 255, 255, 0.9)' }}>
          Schedule Group Supervision Session
        </DialogTitle>
        <DialogContent>
          {error && (
            <div className="mb-4 p-3 glass-card-light border-l-4 border-red-400">
              <Typography variant="body2" className="text-red-600 font-semibold">
                {error}
              </Typography>
            </div>
          )}

          <Box sx={{ mt: 2 }}>
            {/* Supervisor Name and Email - Inline */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium', color: 'rgba(255, 255, 255, 0.9)' }}>
                  Supervisor Name *
                </Typography>
                <input
                  type="text"
                  value={formData.supervisorName}
                  onChange={(e) => setFormData({ ...formData, supervisorName: e.target.value })}
                  placeholder="Enter supervisor name"
                  className="theme-input w-full"
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium', color: 'rgba(255, 255, 255, 0.9)' }}>
                  Supervisor Email *
                </Typography>
                <input
                  type="email"
                  value={formData.supervisorEmail}
                  onChange={(e) => setFormData({ ...formData, supervisorEmail: e.target.value })}
                  placeholder="Enter supervisor email"
                  className="theme-input w-full"
                  required
                />
              </Grid>
            </Grid>

            {/* Date & Time Picker */}
            <div className="mb-4">
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium', color: 'rgba(255, 255, 255, 0.9)' }}>
                Session Date & Time *
              </Typography>
              <DatePickerWrapper>
                <DatePicker
                  selected={formData.sessionDate}
                  onChange={(date) => date && setFormData({ ...formData, sessionDate: date })}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="dd/MM/yyyy HH:mm"
                  locale={enGB}
                  placeholderText="Select date and time"
                  minDate={new Date()}
                  required
                />
              </DatePickerWrapper>
            </div>

            {/* Notes */}
            <div className="mb-4">
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium', color: 'rgba(255, 255, 255, 0.9)' }}>
                Notes (Optional)
              </Typography>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Add any additional notes..."
                className="w-full p-3 border rounded-md resize-none theme-input"
                rows={2}
              />
            </div>

            {/* Counsellor Selection */}
            <div className="mb-2">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'rgba(255, 255, 255, 0.9)' }}>
                  Select Counsellors ({selectedCounsellors.length} selected)
                </Typography>
                <Button
                  size="small"
                  onClick={handleSelectAll}
                  sx={{
                    color: 'rgba(100, 181, 246, 0.9)',
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    '&:hover': {
                      backgroundColor: 'rgba(100, 181, 246, 0.1)'
                    }
                  }}
                >
                  {selectedCounsellors.length === counsellors.length ? 'Deselect All' : 'Select All'}
                </Button>
              </Box>

              <div className="glass-card-light" style={{ maxHeight: 250, overflow: 'auto', padding: '12px', borderRadius: '8px' }}>
                {counsellors.length === 0 ? (
                  <Typography className="theme-text-muted" align="center">
                    No active counsellors available
                  </Typography>
                ) : (
                  counsellors.map((counsellor) => (
                    <FormControlLabel
                      key={counsellor._id}
                      control={
                        <Checkbox
                          checked={selectedCounsellors.includes(counsellor._id)}
                          onChange={() => handleCounsellorToggle(counsellor._id)}
                          sx={{
                            color: 'rgba(255, 255, 255, 0.5)',
                            '&.Mui-checked': {
                              color: 'rgba(100, 181, 246, 0.9)'
                            }
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                            {counsellor.firstName} {counsellor.lastName}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                            {counsellor.email} | {counsellor.phone}
                          </Typography>
                        </Box>
                      }
                      sx={{ display: 'block', mb: 1 }}
                    />
                  ))
                )}
              </div>
            </div>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={handleCloseDialog}
            disabled={loading}
            sx={{ 
              color: 'rgba(255, 255, 255, 0.9)',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: 'rgba(76, 175, 80, 0.2)',
              color: 'rgba(255, 255, 255, 0.9)',
              border: '1px solid rgba(76, 175, 80, 0.3)',
              '&:hover': {
                backgroundColor: 'rgba(76, 175, 80, 0.3)',
                borderColor: 'rgba(76, 175, 80, 0.5)'
              },
              '&:disabled': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'rgba(255, 255, 255, 0.3)',
                borderColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            {loading ? <CircularProgress size={24} sx={{ color: 'rgba(255, 255, 255, 0.5)' }} /> : 'Issue Session'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GroupSupervisionManagement;
